// ============================================================
// META PIXEL + CAPI-READY EVENT LAYER
// ============================================================
// Fires standard Meta e-commerce events with deduplication IDs
// for server-side Conversions API (CAPI) pairing.
//
// Events fired:
//   PageView       — every page load
//   ViewContent    — landing page & product pages
//   AddToCart      — when user clicks Add to Cart
//   InitiateCheckout — when user proceeds to Shopify checkout
//
// CAPI readiness:
//   - Every event gets a unique event_id (UUID v4)
//   - event_id is stored so CAPI can deduplicate
//   - User data (fbp, fbc cookies) forwarded for matching
//   - All custom_data follows Meta's parameter spec
// ============================================================

(function () {
  'use strict';

  var cfg = window.STORE_CONFIG || {};
  var pixelId = (cfg.meta && cfg.meta.pixelId) || '';

  // ---- UUID v4 generator for event deduplication ----
  function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // ---- Cookie helpers ----
  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : '';
  }

  // ---- Extract fbclid from URL for fbc cookie ----
  function captureFbclid() {
    var params = new URLSearchParams(window.location.search);
    var fbclid = params.get('fbclid');
    if (fbclid && /^[a-zA-Z0-9_-]{1,500}$/.test(fbclid)) {
      var fbc = 'fb.1.' + Date.now() + '.' + fbclid;
      var secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = '_fbc=' + fbc + ';path=/;max-age=' + (90 * 86400) + ';SameSite=Lax' + secure;
    }
  }

  // ---- Extract UTM params and store for attribution ----
  function captureUtmParams() {
    var params = new URLSearchParams(window.location.search);
    var utms = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
      var val = params.get(key);
      if (val && val.length <= 500) {
        utms[key] = val.replace(/[<>"']/g, '');
      }
    });
    if (Object.keys(utms).length > 0) {
      sessionStorage.setItem('bw_utms', JSON.stringify(utms));
    }
  }

  // ---- Get user data for CAPI matching ----
  function getUserData() {
    return {
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      client_user_agent: navigator.userAgent,
      event_source_url: window.location.href,
    };
  }

  // ---- Initialize Meta Pixel ----
  function initPixel() {
    if (!pixelId) {
      console.warn('[Meta Pixel] No pixel ID configured in STORE_CONFIG.meta.pixelId');
      return;
    }

    // Standard Meta Pixel base code
    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    // Self-hosted fbevents.js — no third-party script injection
    var basePath = document.querySelector('script[src*="meta-events"]');
    var jsDir = basePath ? basePath.src.replace(/meta-events\.js.*$/, '') : 'js/';
    })(window, document, 'script', jsDir + 'fbevents.js');

    fbq('init', pixelId);
  }

  // ---- Core event dispatcher ----
  // Fires via fbq AND stores event data for CAPI forwarding
  function trackEvent(eventName, customData, options) {
    var eventId = uuid();
    var timestamp = Math.floor(Date.now() / 1000);
    var userData = getUserData();

    // Build the CAPI-ready event payload (stored for server pickup)
    var capiEvent = {
      event_name: eventName,
      event_id: eventId,
      event_time: timestamp,
      event_source_url: window.location.href,
      action_source: 'website',
      user_data: userData,
      custom_data: customData || {},
    };

    // Store in dataLayer for GTM/Stape.io/CAPI gateway pickup
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'meta_' + eventName.toLowerCase(),
      meta_event: capiEvent,
    });

    // Also store in a dedicated array for direct CAPI access
    window._metaEvents = window._metaEvents || [];
    window._metaEvents.push(capiEvent);

    // Fire client-side pixel with event_id for dedup
    if (typeof fbq === 'function') {
      var pixelData = Object.assign({}, customData || {});
      var method = (options && options.custom) ? 'trackCustom' : 'track';

      if (method === 'track') {
        fbq(method, eventName, pixelData, { eventID: eventId });
      } else {
        fbq(method, eventName, pixelData, { eventID: eventId });
      }
    }

    // Console log in dev for debugging
    if (!pixelId || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('[Meta Event]', eventName, { eventId: eventId, data: customData });
    }

    return eventId;
  }

  // ---- Public API ----
  window.MetaEvents = {

    // Fire on every page load (called automatically below)
    pageView: function () {
      trackEvent('PageView');
    },

    // Fire on landing page and product pages
    viewContent: function (product) {
      var data = {
        content_ids: [product.contentId || ''],
        content_name: product.name || '',
        content_type: product.contentType || 'product',
        content_category: product.category || '',
        value: product.value || 0,
        currency: product.currency || 'USD',
      };
      return trackEvent('ViewContent', data);
    },

    // Fire when user clicks Add to Cart
    addToCart: function (product) {
      var data = {
        content_ids: [product.contentId || ''],
        content_name: product.name || '',
        content_type: 'product',
        contents: [{
          id: product.variantId || product.contentId || '',
          quantity: product.quantity || 1,
          item_price: product.price || 0,
        }],
        value: product.value || product.price || 0,
        currency: product.currency || 'USD',
        num_items: product.quantity || 1,
      };
      return trackEvent('AddToCart', data);
    },

    // Fire when redirecting to Shopify checkout
    initiateCheckout: function (cart) {
      var data = {
        content_ids: cart.contentIds || [],
        content_type: 'product',
        contents: cart.contents || [],
        value: cart.value || 0,
        currency: cart.currency || 'USD',
        num_items: cart.numItems || 1,
      };
      return trackEvent('InitiateCheckout', data);
    },

    // Custom: variant selected (for optimization signals)
    variantSelected: function (product) {
      var data = {
        content_ids: [product.contentId || ''],
        content_name: product.name || '',
        variant_label: product.variantLabel || '',
        subscription: product.subscription || false,
        value: product.value || 0,
        currency: product.currency || 'USD',
      };
      return trackEvent('VariantSelected', data, { custom: true });
    },

    // Custom: subscribe toggled
    subscribeToggled: function (subscribed, product) {
      var data = {
        content_ids: [product.contentId || ''],
        subscription_enabled: subscribed,
        value: product.value || 0,
        currency: product.currency || 'USD',
      };
      return trackEvent('SubscribeToggled', data, { custom: true });
    },

    // Utility: get stored UTMs
    getUtms: function () {
      try {
        return JSON.parse(sessionStorage.getItem('bw_utms') || '{}');
      } catch (e) {
        return {};
      }
    },
  };

  // ---- Auto-initialize ----
  captureFbclid();
  captureUtmParams();
  initPixel();

  // Fire PageView on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      MetaEvents.pageView();
    });
  } else {
    MetaEvents.pageView();
  }
})();
